import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
  Alert,
  Collapse,
  Grid,
  IconButton,
  Box,
  Tooltip,
  Typography,
  TextField
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import axios from 'src/axios'; // Your customised axios instance
import HistoryIcon from '@mui/icons-material/History';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  APICONSTANTS,
  CONSTANTS,
  HPO_CONSTANTS,
  PEDIGREE,
  isUnrecognisedCarrierStatus
} from 'src/components/Constants';
import { SnackContext, AppContext, ThemeContext } from 'src/store/ContextStore';
import {
  getHPOSearchURL,
  getGenotypeDisplayParts,
  getPersonAge
} from 'src/util/util';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import { useLoggedInUserName } from 'src/hooks/useLoggedInUserName';
import { useStaffMemberNames } from 'src/hooks/useStaffMemberNames';
import GenotypeModal from './GenotypeModal';
import { EVENTS } from 'src/components/admin/appReducer';
import { TILE_COLOURS } from '../tileColours';
import InlineRecordCapture from '../InlineRecordCapture';

// ------------------ Main GenotypesMenu Component ------------------ //
const GenotypesMenu = ({
  data,
  setMemberRecords,
  memberRecords,
  setPedigreeState,
  modificationHistoryHandle,
  onDirtyChange,
  saveRef,
  discardRef,
  isDisabled
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [options, setOptions] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [totalCount, setTotalCount] = useState(null);
  const [page, setPage] = useState(HPO_CONSTANTS['initialPage']);
  const [loading, setLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedGenotype, setSelectedGenotype] = useState(null);
  const [expandedNewIds, setExpandedNewIds] = useState(new Set());

  const { setSnack } = useContext(SnackContext);
  const { content, dispatchUpdate } = useContext(AppContext);
  const { theme } = useContext(ThemeContext);
  const { pedigreeSelectedUsers: users } = content;
  const { selectedUser: user } = users;
  const t = useAutoTranslation();
  // Who is creating the record — the signed-in clinician, not the proband
  // this chart belongs to.
  const currentUserName = useLoggedInUserName();
  const staffMemberNames = useStaffMemberNames();

  const user_id = useMemo(() => user?.user_id || user?._id, [user]);
  const familyMemberId = useMemo(() => data?.familyMemberId, [data]);
  const proBandId = useMemo(() => data?.proBandId, [data]);

  // Dirty when any selected item has not yet been saved
  const hasUnsaved = selectedItems.some((g) => !g.isSaved);
  useEffect(() => {
    onDirtyChange?.(hasUnsaved);
  }, [hasUnsaved]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (discardRef)
      discardRef.current = () => {
        setSelectedItems((prev) => prev.filter((g) => g.isSaved));
        setSearchTerm('');
      };
  });

  const fetchDataByGenes = async (currentPage) => {
    if (searchTerm.length >= 3) {
      const url = getHPOSearchURL(
        searchTerm,
        currentPage,
        HPO_CONSTANTS.category['gene']
      );
      try {
        const response = await axios.get(url);
        if (isError) setIsError(false);

        const { results = [], totalCount: count } = response.data || {};
        const newOptions = results.map((d) => ({
          id: d.id,
          name: d.name
        }));

        if (currentPage === HPO_CONSTANTS['initialPage']) {
          setOptions(newOptions);
        } else {
          setOptions((preOption) => [...preOption, ...newOptions]);
        }
        setTotalCount(count);
        setPage(currentPage);
      } catch (err) {
        setIsError(true);
        console.error('Error fetching genes:', err);
      }
    } else {
      setOptions([]);
    }
  };

  const getGenotypes = () => {
    const id = proBandId ?? familyMemberId;
    if (!id || !user_id) return;

    // Always look up the freshest data from memberRecords first
    const currentMember = memberRecords.find((m) => m.pid === data?.pid);
    const memberGenotypes = (
      currentMember?.genotypes ||
      data?.genotypes ||
      []
    ).map((g) => ({ ...g, isSaved: true }));

    // Always reset (clears stale data from a previously selected node)
    setSelectedItems(memberGenotypes);

    axios
      .get(
        APICONSTANTS.getGenotypesById
          .replace(':user_id', user_id)
          .replace(':familyMemberId', id),
        {
          params: { isProband: Boolean(proBandId) }
        }
      )
      .then(({ data: responseData }) => {
        const { genotypes = [] } = responseData || {};
        const savedGenotypes = genotypes.map((g) => ({ ...g, isSaved: true }));
        // Use API data if available; otherwise keep what was seeded from memberRecords
        if (savedGenotypes.length > 0) {
          setSelectedItems(savedGenotypes);
        }
      })
      .catch((err) => {
        console.error('Error fetching genotypes:', err);
      });
  };

  useEffect(() => {
    getGenotypes();
    setExpandedNewIds(new Set());
    // eslint-disable-next-line
  }, [familyMemberId, proBandId]);

  useEffect(() => {
    const memberData = proBandId
      ? users?.selectedUser
      : (users?.family || []).find((m) => m.familyMemberId === familyMemberId);
    const incoming = (memberData?.genotypes || []).map((g) => ({
      ...g,
      isSaved: true
    }));
    if (!incoming.length) return;
    setSelectedItems((prev) => {
      const savedIds = new Set(prev.filter((g) => g.isSaved).map((g) => g.id));
      if (incoming.every((g) => savedIds.has(g.id))) return prev;
      const pending = prev.filter((g) => !g.isSaved);
      return [
        ...incoming,
        ...pending.filter((p) => !incoming.some((g) => g.id === p.id))
      ];
    });
  }, [users?.selectedUser?.genotypes, users?.family]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchDataByGenes(HPO_CONSTANTS['initialPage']);
    // eslint-disable-next-line
  }, [searchTerm]);

  const updateData = (records) => {
    setMemberRecords([...records]);
    setPedigreeState((prev) => ({
      ...prev,
      save: PEDIGREE.saveStatus.IN_PROGRESS
    }));
  };

  const addAndSaveGenotypes = (newItems) => {
    const id = proBandId ?? familyMemberId;
    if (!id || !user_id) {
      return setSnack({
        status: true,
        msg: 'Please save the pedigree first',
        severity: 'error'
      });
    }

    setLoading(true);
    setExpandedNewIds(
      (prev) => new Set([...prev, ...newItems.map((g) => g.id)])
    );

    const payload = {
      familyId: user.familyId,
      familyMemberId: id,
      user_id: user_id,
      isProband: Boolean(proBandId),
      genotypes: newItems
    };

    return axios
      .post(APICONSTANTS.genotypes, payload)
      .then((res) => {
        if (res.data?.success) {
          const savedGenotypes = res.data.genotypes.map((g) => ({
            ...g,
            isSaved: true
          }));
          setSelectedItems(savedGenotypes);

          const new_dataset = memberRecords.map((elem) => {
            if (elem.pid === data.pid) {
              return { ...elem, genotypes: res.data.genotypes };
            }
            return elem;
          });
          updateData(new_dataset);

          dispatchUpdate({
            type: EVENTS.SET_PEDIGREE_GENOTYPES,
            value: res.data.genotypes
          });
        } else {
          setSnack({
            status: true,
            msg: res.data.message || 'Error saving genotypes',
            severity: 'error'
          });
        }
      })
      .catch((err) => {
        setSnack({
          status: true,
          msg: err.response?.data?.error || 'An error has occurred!',
          severity: 'error'
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const inlineGenotypeSave = (updatedGenotype) => {
    const id = proBandId ?? familyMemberId;
    const { isSaved, ...genotypeData } = updatedGenotype;
    setSelectedItems((prev) =>
      prev.map((item) =>
        item.id === updatedGenotype.id
          ? { ...updatedGenotype, isSaved: true }
          : item
      )
    );
    return axios
      .put(
        APICONSTANTS.updateGenotype
          .replace(':user_id', user_id)
          .replace(':familyMemberId', id),
        {
          genotype: genotypeData,
          isProband: Boolean(proBandId)
        }
      )
      .then((response) => {
        if (response.data.success) {
          const new_dataset = memberRecords.map((elem) => {
            if (elem.pid === data.pid) {
              return {
                ...elem,
                genotypes: (elem.genotypes || []).map((g) =>
                  g.id === updatedGenotype.id ? genotypeData : g
                )
              };
            }
            return elem;
          });
          updateData(new_dataset);
          setSnack({
            status: true,
            msg: 'Genotype updated successfully!',
            severity: 'success'
          });
        }
      })
      .catch((err) => {
        console.error('Error updating genotype:', err);
        setSnack({
          status: true,
          msg: 'Error updating genotype',
          severity: 'error'
        });
      });
  };

  const handleSelect = (event, value) => {
    if (!value) return;

    const newItems = [];
    value.forEach((newItem) => {
      const alreadySelected = selectedItems.some((m) => m.id === newItem.id);
      if (!alreadySelected) {
        newItems.push({
          id: newItem.id,
          name: newItem.name,
          clinicallyConfirmed: false,
          patientReported: false,
          isPrivate: false,
          displayOnPedigree: true,
          dateRecorded: new Date().toISOString().split('T')[0],
          recordedBy: currentUserName,
          carrierStatus: '',
          selectedClinVarIds: []
        });
      }
    });
    if (newItems.length > 0) {
      addAndSaveGenotypes(newItems);
    }
    setSearchTerm('');
  };

  const handleTileClick = (genotype) => {
    setSelectedGenotype(genotype);
    setModalOpen(true);
  };

  const handleModalSave = (updatedFields) => {
    const updatedGenotype = { ...selectedGenotype, ...updatedFields };

    setSelectedItems((prev) =>
      prev.map((item) =>
        item.id === updatedGenotype.id ? updatedGenotype : item
      )
    );

    const id = proBandId ?? familyMemberId;

    // Check if genotype exists in database
    if (updatedGenotype.isSaved) {
      const { isSaved, ...genotypeData } = updatedGenotype;

      axios
        .put(
          APICONSTANTS.updateGenotype
            .replace(':user_id', user_id)
            .replace(':familyMemberId', id),
          {
            genotype: genotypeData,
            isProband: Boolean(proBandId)
          }
        )
        .then((response) => {
          if (response.data.success) {
            setSnack({
              status: true,
              msg: 'Genotype updated successfully!',
              severity: 'success'
            });

            const updatedGenotypes = selectedItems.map((item) =>
              item.id === updatedGenotype.id ? updatedGenotype : item
            );

            const new_dataset = memberRecords.map((elem) => {
              if (elem.pid === data.pid) {
                return { ...elem, genotypes: updatedGenotypes };
              }
              return elem;
            });

            updateData(new_dataset);

            dispatchUpdate({
              type: EVENTS.SET_PEDIGREE_GENOTYPES,
              value: updatedGenotypes
            });
          } else {
            setSnack({
              status: true,
              msg: response.data.message || 'Error updating genotype',
              severity: 'error'
            });
          }
        })
        .catch((err) => {
          console.error('Error updating genotype:', err);
          setSnack({
            status: true,
            msg: 'Error updating genotype',
            severity: 'error'
          });
        })
        .finally(() => {
          setModalOpen(false);
        });
    } else {
      const { isSaved, ...genotypeData } = updatedGenotype;

      const payload = {
        familyId: user.familyId,
        familyMemberId: id,
        user_id: user_id,
        isProband: Boolean(proBandId),
        genotypes: [genotypeData]
      };

      axios
        .post(APICONSTANTS.genotypes, payload)
        .then((res) => {
          if (res.data?.success) {
            const savedGenotype = res.data.genotypes.find(
              (g) => g.id === updatedGenotype.id
            );

            const allSavedGenotypes = res.data.genotypes.map((g) => ({
              ...g,
              isSaved: true
            }));

            setSelectedItems((prev) =>
              prev.map((item) => {
                const savedVersion = allSavedGenotypes.find(
                  (g) => g.id === item.id
                );
                return savedVersion || item;
              })
            );

            const new_dataset = memberRecords.map((elem) => {
              if (elem.pid === data.pid) {
                return {
                  ...elem,
                  genotypes: res.data.genotypes
                };
              }
              return elem;
            });

            updateData(new_dataset);

            dispatchUpdate({
              type: EVENTS.SET_PEDIGREE_GENOTYPES,
              value: res.data.genotypes
            });

            setSnack({
              status: true,
              msg: 'Genotype saved successfully!',
              severity: 'success'
            });
          } else {
            setSnack({
              status: true,
              msg: res.data.message || 'Error saving genotype',
              severity: 'error'
            });
          }
        })
        .catch((err) => {
          console.error('Error saving genotype:', err);
          setSnack({
            status: true,
            msg: err.response?.data?.error || 'Error saving genotype',
            severity: 'error'
          });
        })
        .finally(() => {
          setModalOpen(false);
        });
    }
  };

  const onSave = () => {
    const id = proBandId ?? familyMemberId;
    if (!id || !user_id) {
      return setSnack({
        status: true,
        msg: 'Kindly save pedigree first!',
        severity: 'error'
      });
    }

    setLoading(true);
    // Remove local tracking flag before sending to API
    const genotypesData = selectedItems.map(({ isSaved, ...rest }) => rest);

    const payload = {
      familyId: user.familyId,
      familyMemberId: id,
      user_id: user_id,
      isProband: Boolean(proBandId),
      genotypes: genotypesData
    };

    return axios
      .post(APICONSTANTS.genotypes, payload)
      .then((res) => {
        if (res.data?.success) {
          const new_dataset = memberRecords.map((elem) => {
            if (elem.pid === data.pid) {
              return {
                ...elem,
                genotypes: res.data.genotypes
              };
            }
            return elem;
          });

          updateData(new_dataset);

          // Mark all genotypes as saved
          const savedGenotypes = res.data.genotypes.map((g) => ({
            ...g,
            isSaved: true
          }));
          setSelectedItems(savedGenotypes);

          dispatchUpdate({
            type: EVENTS.SET_PEDIGREE_GENOTYPES,
            value: res.data.genotypes
          });

          setSnack({
            status: true,
            msg: 'Genotypes saved successfully',
            severity: 'success'
          });
        } else {
          setSnack({
            status: true,
            msg: res.data.message || 'Error saving genotypes',
            severity: 'error'
          });
        }
      })
      .catch((err) =>
        setSnack({
          status: true,
          msg: err.response?.data?.error || 'An error has occurred!',
          severity: 'error'
        })
      )
      .finally(() => setLoading(false));
  };

  // Keep saveRef current so parent can trigger save with latest state
  useEffect(() => {
    if (saveRef) saveRef.current = onSave;
  });

  const handleScroll = (event) => {
    const target = event.target;
    if (
      target.scrollHeight - target.scrollTop === target.clientHeight &&
      options.length < totalCount
    ) {
      const newCurrentPage = page + 1;
      fetchDataByGenes(newCurrentPage);
    }
  };

  const onDeleteGenotype = async (genotypeId) => {
    const id = proBandId ?? familyMemberId;
    if (!id || !user_id) return;

    return axios
      .delete(
        APICONSTANTS.deleteGenotypeById
          .replace(':user_id', user_id)
          .replace(':familyMemberId', id),
        {
          data: {
            genotypeId,
            isProband: proBandId ? true : false
          }
        }
      )
      .then((res) => {
        const new_dataset = memberRecords.map((elem) => {
          if (elem.pid === data.pid) {
            return {
              ...elem,
              genotypes: elem.genotypes.filter(
                (genotype) => genotype.id !== genotypeId
              )
            };
          }
          return elem;
        });

        updateData(new_dataset);

        setSelectedItems(
          selectedItems.filter((genotype) => genotype.id !== genotypeId)
        );
        setSnack({
          status: true,
          msg: 'Genotypes deleted successfully',
          severity: 'success'
        });
        setModalOpen(false);
      })
      .catch((err) => {
        setSnack({
          status: true,
          msg: err.response?.data?.message ?? 'An error has occurred!',
          severity: 'error'
        });
      });
  };

  return (
    <>
      <Grid container spacing={2}>
        {isError && (
          <Grid size={12}>
            <Alert severity="warning">
              {t(
                'The terminology service is unreachable. Please contact admin.'
              )}
            </Alert>
          </Grid>
        )}

        <Grid size={12}>
          <Autocomplete
            multiple
            clearOnBlur={false}
            id="tags-outlined"
            options={options}
            value={[]}
            getOptionLabel={(option) => option.name}
            onChange={handleSelect}
            filterOptions={(options) => options}
            disabled={isDisabled}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search for Genes"
                data-va-anchor="pedigree.genotypes.search"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            )}
            slotProps={{
              listbox: { onScroll: handleScroll }
            }}
          />
        </Grid>

        {selectedItems.map((item) => {
          const isExpanded = item.isSaved && expandedNewIds.has(item.id);
          return (
            <Grid key={item.id} size={12}>
              <Box
                sx={{
                  border: `1px solid ${TILE_COLOURS.genotype.border}`,
                  borderLeft: `3px solid ${TILE_COLOURS.genotype.accent}`,
                  padding: '8px 10px',
                  borderRadius: isExpanded ? '6px 6px 0 0' : '6px',
                  backgroundColor: TILE_COLOURS.genotype.background,
                  cursor: item.isSaved && !isExpanded ? 'pointer' : 'default',
                  ...(!item.isSaved
                    ? {
                        backgroundColor: '#FFF3E0',
                        color: '#BA7517',
                        border: '1px solid #BA7517',
                        borderLeft: '3px solid #BA7517'
                      }
                    : {}),
                  ...(isExpanded ? { borderBottom: 'none' } : {})
                }}
                onClick={() =>
                  item.isSaved && !isExpanded && handleTileClick(item)
                }
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: item.isSaved
                        ? TILE_COLOURS.genotype.text
                        : 'inherit'
                    }}
                  >
                    {getGenotypeDisplayParts(item).symbol || 'N/A'}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {/* Carrier status we cannot interpret. The pedigree label still
                        shows the value verbatim, but no +/- marker is derived from it,
                        which is otherwise invisible — the label looks correct. Same
                        visual language as the lab/ClinVar discrepancy warning on
                        VariantRow: something in the record is not fully represented on
                        the pedigree, so check the source. */}
                    {isUnrecognisedCarrierStatus(item.carrierStatus) && (
                      <Tooltip
                        title={`Carrier status "${item.carrierStatus}" was not recognised, so no carrier symbol has been derived on the pedigree. Check the source record.`}
                      >
                        <WarningAmberIcon
                          onClick={(e) => e.stopPropagation()}
                          color="warning"
                          sx={{ fontSize: 18, cursor: 'help' }}
                        />
                      </Tooltip>
                    )}
                    {item.comment && (
                      <Tooltip
                        title={
                          <Box sx={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>
                            {item.comment}
                          </Box>
                        }
                      >
                        <StickyNote2OutlinedIcon
                          onClick={(e) => e.stopPropagation()}
                          sx={{
                            color: TILE_COLOURS.genotype.accent,
                            fontSize: 18,
                            cursor: 'help'
                          }}
                        />
                      </Tooltip>
                    )}
                    {item.clinicallyConfirmed && (
                      <CheckCircleOutlineIcon
                        style={{ color: 'green', fontSize: 20 }}
                      />
                    )}
                  </Box>
                </div>
                <Typography
                  variant="body2"
                  sx={{
                    color: item.isSaved
                      ? TILE_COLOURS.genotype.subtext
                      : 'inherit'
                  }}
                >
                  {typeof item.id === 'string' &&
                  item.id.startsWith('NCBIGene:')
                    ? item.id
                    : ''}
                </Typography>
              </Box>
              {item.isSaved && (
                <Collapse in={isExpanded} unmountOnExit>
                  <InlineRecordCapture
                    record={item}
                    patientDob={data?.dob}
                    maxAge={getPersonAge(data)}
                    dateField="dateRecorded"
                    ageField="recordedAge"
                    dateFormat={theme.dateFormat}
                    dateLabel="Date Recorded"
                    ageLabel="Recorded Age"
                    onSaveCapture={(update) => inlineGenotypeSave(update)}
                    onSkip={() => {
                      setExpandedNewIds((prev) => {
                        const next = new Set(prev);
                        next.delete(item.id);
                        return next;
                      });
                    }}
                  />
                </Collapse>
              )}
            </Grid>
          );
        })}

        <Grid size={12}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            {selectedItems.some((g) => g.clinicallyConfirmed) ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}
              >
                <CheckCircleOutlineIcon
                  style={{ color: 'green', fontSize: 20 }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 'bold'
                  }}
                >
                  Clinically Confirmed
                </Typography>
              </Box>
            ) : (
              <span />
            )}
            <IconButton
              size="small"
              onClick={() =>
                modificationHistoryHandle(CONSTANTS.auditLogType['genotypes'])
              }
            >
              <HistoryIcon />
            </IconButton>
          </Box>
        </Grid>

        {selectedGenotype && (
          <GenotypeModal
            key={`${selectedGenotype.id}::${proBandId || familyMemberId}`}
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            genotype={selectedGenotype}
            onSave={handleModalSave}
            currentUserName={currentUserName}
            staffMemberNames={staffMemberNames}
            onDeleteGenotype={onDeleteGenotype}
            isDisabled={isDisabled}
            patientDob={data?.dob}
            fetchFreshEntity={async () => {
              const id = proBandId ?? familyMemberId;
              if (!id || !user_id || !selectedGenotype?.id) return null;
              const { data: responseData } = await axios.get(
                APICONSTANTS.getGenotypesById
                  .replace(':user_id', user_id)
                  .replace(':familyMemberId', id),
                { params: { isProband: Boolean(proBandId) } }
              );
              const fresh = (responseData?.genotypes || []).find(
                (g) => g.id === selectedGenotype.id
              );
              return fresh ? { ...fresh, isSaved: true } : null;
            }}
            onShowHistory={
              selectedGenotype?.id && modificationHistoryHandle
                ? () =>
                    modificationHistoryHandle(
                      CONSTANTS.auditLogType.genotypes,
                      {},
                      selectedGenotype.id
                    )
                : null
            }
          />
        )}
      </Grid>
    </>
  );
};

export default GenotypesMenu;
